import {
  defined,
  isSameArray,
  Optional,
  range,
  removeDuplicated,
  returnIf,
} from '../utils/Util';
import { IntRange, RaceIdRanges, RacesViewData } from './RaceDataUtil';

const CHECK_MODE = {
  DEFAULT: 'default',
  NO_MIN: 'noMin',
  NO_MAX: 'noMax',
};

type CheckMode = typeof CHECK_MODE[keyof typeof CHECK_MODE];

export class RaceIdRangesConverter {
  private readonly cache: Record<string, [boolean, IntRange[]]> = {};

  constructor(readonly racesViewData: RacesViewData) {}

  convertForReadability(ranges: RaceIdRanges): RaceIdRanges {
    const resultRanges = this.separateByYear(ranges).reduce((ranges, range) => {
      ranges.push(...this.separateByRound(range));
      return ranges;
    }, [] as IntRange[]);
    return new RaceIdRanges(resultRanges);
  }

  private separateByYear(ranges: RaceIdRanges): IntRange[] {
    return ranges.ranges.reduce<IntRange[]>(
      (intervals, interval, index, array) => {
        const startYear = this.racesViewData.get(interval.start)?.year;
        const endYear = this.racesViewData.get(interval.end)?.year;
        if (defined(startYear) && defined(endYear) && startYear !== endYear) {
          // 範囲の先頭と末尾が異なるなら分割対象
          // 分割位置。setなのは先頭と末尾が1年違いのとき、
          // 下記のlastとfirstが同じ値で重複するのを取り除くため
          const endRaceIds = new Set<number>();

          if (
            this.racesViewData.get(array[index - 1]?.end)?.year === startYear
          ) {
            // 前の範囲の最後が別年、あるいは前の範囲が存在しないなら分ける必要なし
            const last = this.racesViewData.getLastRaceId(startYear);
            endRaceIds.add(last);
          }
          if (
            this.racesViewData.get(array[index + 1]?.start)?.year === endYear
          ) {
            // 次の範囲の先頭が別年、あるいは前の範囲が存在しないなら分ける必要なし
            const first = this.racesViewData.getFirstRaceId(endYear);
            endRaceIds.add(first - 1);
          }
          return [
            ...intervals,
            ...this.separateByEndRaceIds(interval, endRaceIds),
          ];
        } else {
          // 範囲の先頭と末尾が同じなら分ける必要なし
          return [...intervals, interval];
        }
      },
      []
    );
  }

  private separateByEndRaceIds(
    range: IntRange,
    endRaceIds: Iterable<number> | ArrayLike<number>
  ): IntRange[] {
    const ids = Array.from(endRaceIds).sort((a, b) => a - b);
    return [...ids, range.end].map((end, index, array) => {
      const start = returnIf(array[index - 1], (s) => s + 1) ?? range.start;
      return new IntRange(start, end);
    });
  }

  private separateByRound(intRange: IntRange): IntRange[] {
    const racesViewData = this.racesViewData;
    const raceIds = intRange.toFilledArray();

    // 前方
    const formerRanges: IntRange[] = [];
    while (raceIds.length > 0) {
      const firstRaceId = raceIds[0] as number; // 要素数1以上なので確実に取れる

      // 先頭のレースの年
      const currentYear = racesViewData.get(firstRaceId)?.year;
      if (!defined(currentYear)) {
        // never happen
        continue;
      }
      // raceIds のうち、currentYear が示す年の最後のレース
      let lastRaceId = firstRaceId;
      for (const raceId of raceIds.slice(1)) {
        if (racesViewData.get(raceId)?.year !== currentYear) {
          break;
        } else {
          lastRaceId = raceId;
        }
      }

      const raceIdsInYear = new IntRange(firstRaceId, lastRaceId);
      // 翌年以降と結合できるか、また結合しない場合の配列
      const [canJoin, separatedRaceIds] = this.separateByRoundPart(
        raceIdsInYear,
        lastRaceId === raceIds.at(-1) ? CHECK_MODE.DEFAULT : CHECK_MODE.NO_MAX
      );
      if (canJoin) {
        break;
      }
      formerRanges.push(...separatedRaceIds);
      // lastRaceId も含めて消すには +1 が必要
      raceIds.splice(0, raceIds.indexOf(lastRaceId) + 1);
    }

    // 後方
    const latterRanges: IntRange[] = [];
    while (raceIds.length > 0) {
      const lastRaceId = raceIds.at(-1) as number; // 要素数1以上なので確実に取れる
      const currentYear = racesViewData.get(lastRaceId)?.year;
      if (!defined(currentYear)) {
        // never happen
        continue;
      }
      // raceIds のうち、currentYear が示す年の最初のレース
      let firstRaceId = lastRaceId;
      for (const raceId of raceIds.slice(0, -1).reverse()) {
        if (racesViewData.get(raceId)?.year !== currentYear) {
          break;
        } else {
          firstRaceId = raceId;
        }
      }

      const raceIdsInYear = new IntRange(firstRaceId, lastRaceId);
      // 前年以降と結合できるか、また結合しない場合の配列
      const [canJoin, separatedRaceIds] = this.separateByRoundPart(
        raceIdsInYear,
        firstRaceId === raceIds[0] ? CHECK_MODE.DEFAULT : CHECK_MODE.NO_MIN
      );
      if (canJoin) {
        break;
      }
      latterRanges.push(...separatedRaceIds);
      // lastRaceId も含めて消すには +1 が必要
      raceIds.splice(
        raceIds.indexOf(firstRaceId),
        raceIds.length - raceIds.indexOf(firstRaceId) + 1
      );
    }

    const remainedFirstRaceId = raceIds[0];
    const remainedLastRaceId = raceIds.at(-1);
    const remainedRanges =
      defined(remainedFirstRaceId) && defined(remainedLastRaceId)
        ? new IntRange(remainedFirstRaceId, remainedLastRaceId)
        : [];
    return formerRanges.concat(remainedRanges).concat(latterRanges);
  }

  private separateByRoundPart(
    raceIdsInSameYear: IntRange,
    checkMode: CheckMode
  ): [boolean, IntRange[]] {
    const key = [
      raceIdsInSameYear.start,
      raceIdsInSameYear.end,
      checkMode,
    ].join('_');
    let result = this.cache[key];
    if (!defined(result)) {
      result = this.separateByRoundPartImpl(raceIdsInSameYear, checkMode);
      this.cache[key] = result;
    }
    return result;
  }

  /**
   *
   * @param raceIdsInSameYear: raceId は全て同じ年である必要がある
   * @param racesViewData
   * @param checkMode: この raceIds の前方や後方に結合する別な raceId があるかどうか
   * @returns
   */
  private separateByRoundPartImpl(
    raceIdsInSameYear: IntRange,
    checkMode: CheckMode
  ): [boolean, IntRange[]] {
    const racesViewData = this.racesViewData;
    const defaultResult = [true, [raceIdsInSameYear]] as [boolean, IntRange[]];
    // IntRange に含まれる raceId が示す実際のラウンド
    // スプリントレースの場合は同一ラウンドのレースが複数含まれる可能性がある
    const actualRounds = removeDuplicated(
      raceIdsInSameYear
        .toFilledArray()
        .map((id) => racesViewData.get(id)?.round)
        .filter((r) => defined(r))
    );
    const firstRound = actualRounds[0];
    const lastRound = actualRounds.at(-1);
    const currentYear = racesViewData.get(raceIdsInSameYear.start)?.year;
    if (!defined(firstRound) || !defined(lastRound) || !defined(currentYear)) {
      // 本来起きない。例外を上げずに元の配列を返す
      return defaultResult;
    }
    const minRound = racesViewData.getMinRound(currentYear);
    const maxRound = racesViewData.getMaxRound(currentYear);

    // NO_MIN の場合、その年のラウンドで一番小さい値をstartにする
    // 初戦がRd2の場合、~2 が見た目だが、これには1が含まれると見えるため
    const apparentRangeStart =
      checkMode === CHECK_MODE.NO_MIN ? minRound : firstRound;
    // NO_MAX の場合はその年のラウンドで一番大きい値をendにする
    const apparentRangeEnd =
      checkMode === CHECK_MODE.NO_MAX ? maxRound : lastRound;
    // IntRange が示す見た目のラウンド
    const apparentRounds = range(apparentRangeStart, apparentRangeEnd);

    if (
      isSameArray(
        actualRounds.sort((a, b) => a - b),
        apparentRounds
      )
    ) {
      // 見た目に含まれるラウンドと実際に含まれるラウンドが同一ならそのまま返す
      return defaultResult;
    } else {
      if (raceIdsInSameYear.start === raceIdsInSameYear.end) {
        // 例えば NO_MIN が指定され、21345678 で最初の raceId が指定された場合、
        // 見た目は [1, 2] 実際は [2]
        // ⇒ 2 を分離して表示させるため false を返す
        return [false, [raceIdsInSameYear]];
      }
      let candidate: Optional<IntRange[]>;
      const raceIds = raceIdsInSameYear.toFilledArray();
      const firstRaceId = raceIds[0];
      const lastRaceId = raceIds.at(-1);
      // 前半と後半で2グループに分ける。全通りを試す
      // [1, 2, 3] => ([1], [2, 3]), ([1, 2], 3)
      for (const position of range(0, raceIds.length - 2)) {
        const lastOfFormerRaceIds = raceIds[position];
        const firstOfLatterRaceIds = raceIds[position + 1];
        if (
          !defined(firstRaceId) ||
          !defined(lastRaceId) ||
          !defined(lastOfFormerRaceIds) ||
          !defined(firstOfLatterRaceIds)
        ) {
          // 本来起きない。例外を上げずに無視
          continue;
        }
        // 部分列に対して再帰的に結果を得る
        const formerResult = this.separateByRoundPart(
          new IntRange(firstRaceId, lastOfFormerRaceIds),
          checkMode === CHECK_MODE.NO_MIN
            ? CHECK_MODE.NO_MIN
            : CHECK_MODE.DEFAULT
        )[1];
        const latterResult = this.separateByRoundPart(
          new IntRange(firstOfLatterRaceIds, lastRaceId),
          checkMode === CHECK_MODE.NO_MAX
            ? CHECK_MODE.NO_MAX
            : CHECK_MODE.DEFAULT
        )[1];
        if (
          !defined(candidate) ||
          formerResult.length + latterResult.length < candidate.length
        ) {
          // 一番分割数が小さいものを使う
          candidate = formerResult.concat(latterResult);
        }
      }
      if (defined(candidate)) {
        return [false, candidate];
      } else {
        // 本来起きない。例外を上げずに元の配列を返す
        return defaultResult;
      }
    }
  }
}
